import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import clsx from 'clsx'
import { consentActions, consentSelectors } from 'store/consent'
import { useModal } from 'providers/ModalProvider'
import { CookiePreferencesModal } from 'components/modals/CookiePreferencesModal'
import { OdinButton } from '@/components/ui/odin/odin-button'
import { useGetSetting } from 'api/settings'

interface CookieConsentProps {
  /** 'widget' = sits inside the rounded widget box. 'page' = fixed bottom bar on the fullscreen /chat layout. */
  surface?: 'widget' | 'page'
}

export const CookieConsent = ({ surface = 'widget' }: CookieConsentProps) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const decided = useSelector(consentSelectors.decided)
  const { openModal } = useModal()
  const { setting } = useGetSetting()
  const bannerEnabled = setting?.cookieBannerEnabled === true

  // Banner only in opt-in mode; opt-out auto-accepts via useCookieConsentBootstrap.
  if (decided || !bannerEnabled) return null

  const decide = (analytics: boolean) => dispatch(consentActions.setAnalyticsConsent(analytics))

  return (
    <div className={clsx('z-60 p-3', surface === 'page' ? 'fixed right-0 bottom-0' : 'absolute inset-x-0 bottom-0')}>
      <div
        className={clsx(
          'animate-slide-up-modal rounded-2xl border border-border bg-card p-4 shadow-lg',
          surface === 'page' ? 'w-[440px] max-w-[calc(100vw-1.5rem)]' : 'w-full max-w-2xl',
        )}
      >
        <p className="m-0 text-sm font-semibold text-foreground">{t('cookieBanner.title')}</p>
        <p className="mt-1 mb-3 text-xs leading-relaxed text-muted-foreground">
          {t('cookieBanner.message')}{' '}
          <a href="/privacy-policy" target="_blank" rel="noreferrer" className="text-muted-foreground underline">
            {t('cookieBanner.privacy')}
          </a>{' '}
          ·{' '}
          <a href="/cookie-policy" target="_blank" rel="noreferrer" className="text-muted-foreground underline">
            {t('cookieBanner.cookie')}
          </a>
        </p>

        <div className="flex items-center gap-2">
          {/* Accept and Reject share equal prominence (GDPR): same size, side by side. */}
          <OdinButton variant="secondary" className="flex-1" onClick={() => decide(false)}>
            {t('cookieBanner.reject')}
          </OdinButton>
          <OdinButton className="flex-1" onClick={() => decide(true)}>
            {t('cookieBanner.accept')}
          </OdinButton>
        </div>

        <button
          type="button"
          onClick={() => openModal(<CookiePreferencesModal />)}
          className="mt-2 w-full bg-transparent text-center text-xs text-muted-foreground underline"
        >
          {t('cookieBanner.customize')}
        </button>
      </div>
    </div>
  )
}
